import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Table from "../../../Components/Common/Table";
import TableHead from "../../../Components/Common/TableHead";
import { UserApiUrls } from "../../../Api/Auth/ApiUrls";
import useSWR from "swr";
import Modal from "../../../Components/Common/Modal";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import useToastError from "../../../hooks/useErrorToast";
import { toast } from "react-toastify";
import QueryString from "qs";
import useTable from "../../../hooks/useTable";
import { useLocation } from "react-router-dom";
import UserRow from "./Components/UserRow";
import UserForm from "./Components/UserForm";
import { Loader } from "../../../Components/Common/Loader";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import NoData from "../../../Components/Common/NoData";

const tableColumnData = [
  { id: "name", label: "User Name" },
  { id: "email", label: "Email" },
  { id: "organization", label: "Organization" },
  { id: "roleid", label: "Role" },
  { id: "createdAt", label: "Created" },
];

const UserList = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    roleId: null,
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    organization: "",
  });
  const [errors, setErrors] = useState({});
  const { showErrorToast } = useToastError();
  const [row, setRow] = useState(null);
  const location = useLocation();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          organization: "",
        });
        setModalOpen(true);
      },
    },
  ];

  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);
  const { isLoading, data, mutate } = useSWR(UserApiUrls.list(apiQueryString));

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };
  const onEditClick = (row) => {
    setRow({ ...row, password: "" });
    handleInputChange("name", row.name);
    handleInputChange("roleId", row.roleId);
    handleInputChange("email", row.email);
    handleInputChange("organization", row.organization);
    setModalOpen(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.roleId) newErrors.roleId = "User Role is required";
    if (!formData.name) newErrors.name = "Name is required.";
    if (!formData.email) newErrors.email = "Email is required.";
    if (!formData.password) newErrors.password = "Password is required.";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Confirm password is required.";
    else if (formData.confirmPassword !== formData.password)
      newErrors.confirmPassword = "Passwords do not match.";
    if (!formData.organization)
      newErrors.organization = "Organization is required.";
    // Add more validation logic as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { trigger, isMutating } = useSWRMutation(
    UserApiUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    UserApiUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    const modifiedData = { ...formData, roleId: formData.roleId.value };
    delete modifiedData.confirmPassword;

    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <UserRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit User" : "Create User"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <UserForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
        />
      </Modal>
    </div>
  );
};

export default UserList;
